import {
  CheckCircledIcon,
  CrossCircledIcon,
  Pencil1Icon,
} from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  ICustomFieldDefinition,
  IHospitalDetail,
} from "../../../../@types/admin/AdminHospitalDetails";
import { TableSorted } from "../../../../components/TableSorted";
import { useUpdateCustomField } from "../../../../hooks/mutations/admin/hospitalDetails";
import {
  CUSTOM_FIELDS_QUERY_KEY,
  useCustomFields,
} from "../../../../hooks/queries/admin/hospitalDetails";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { errorToastMessage } from "../../../../utils/HttpError";
import { RowActions } from "../../Patients/AdminPatients.styles";
import {
  ErrorMessage,
  FieldHint,
  FilterButton,
  HeaderActions,
  RowAction,
  PageInfoTooltip,
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionTitleGroup,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../../shared";
import { AddFromTemplatesModal } from "./AddFromTemplatesModal";
import { CustomFieldModal } from "./CustomFieldModal";
import { DeactivateCustomFieldModal } from "./DeactivateCustomFieldModal";
import { TabStack } from "./HospitalDetails.styles";

const SEARCH_FIELDS: (keyof ICustomFieldDefinition)[] = ["label", "key"];

interface IHospitalCustomFieldsTabProps {
  hospital: IHospitalDetail;
}

/**
 * Custom fields tab of the Hospital Details page (EN-5175) — Retool's
 * `moduleCustomFields`, which its hospital form opened as
 * `modalFrame_hospitalOnboardingFields`.
 *
 * These are what Retool calls the hospital's "onboarding fields". Note there
 * are three generations of that feature in the codebase and only this one is
 * live:
 *
 *  1. `PUT /Retool/Hospitals/{id}` carries an `enabledOnboardingFields` key.
 *     Retool still sends it, bound to a component that exists nowhere in the
 *     export; `UpdateHospitalRequestDto` has no such property, so it is ignored.
 *  2. `GET`/`PUT /Retool/Hospitals/{id}/OnboardingFields` — a real endpoint that
 *     nothing in the 53 exports and nothing in the portal calls.
 *  3. `/Retool/CustomFields` — this. What the page's own button opens.
 *
 * Inactive definitions are listed rather than filtered out: deactivation is
 * reversible and the operator needs to see what is there to reactivate it.
 */
export function HospitalCustomFieldsTab({
  hospital,
}: IHospitalCustomFieldsTabProps) {
  const hospitalGroupId = Number(hospital.id);
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();
  const { customFields, isLoading, isError } = useCustomFields(hospitalGroupId);
  const { updateCustomField, isUpdatingCustomField } = useUpdateCustomField();

  const { items, allItems, pagination, setPage, setPageSize } =
    useMockPagination({
      data: customFields,
      searchFields: SEARCH_FIELDS,
    });

  const handleReactivate = async (field: ICustomFieldDefinition) => {
    try {
      // Reactivation is the update route with isActive true — there is no
      // separate endpoint, which is why the whole definition goes back.
      await updateCustomField({
        id: field.id,
        hospitalGroupId,
        request: {
          label: field.label,
          type: field.type,
          options: field.options,
          validation: field.validation,
          isActive: true,
        },
      });
      showToast(`${field.label} was reactivated.`, "success");
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error reactivating this custom field."
        ),
        "warning"
      );
    }
  };

  const columns: ColumnDef<ICustomFieldDefinition, string>[] = useMemo(
    () => [
      { accessorKey: "label", header: "Label", enableSorting: true },
      { accessorKey: "key", header: "Key", enableSorting: true },
      { accessorKey: "type", header: "Type", enableSorting: true },
      {
        id: "options",
        header: "Options",
        accessorFn: (field) =>
          field.options.length > 0 ? field.options.join(", ") : "-",
        enableSorting: true,
      },
      {
        id: "isActive",
        header: "State",
        accessorFn: (field) => (field.isActive ? "Active" : "Inactive"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.isActive ? "active" : "inactive"}>
            {row.original.isActive ? "Active" : "Inactive"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Edit"
              icon={<Pencil1Icon />}
              onClick={() => {
                openModal(
                  <CustomFieldModal
                    hospitalGroupId={hospitalGroupId}
                    field={row.original}
                    onDone={closeModal}
                  />,
                  {
                    width: "min(560px, 94vw)",
                    maxHeight: "90vh",
                    showCloseButton: true,
                  }
                );
              }}
            />
            {row.original.isActive ? (
              <RowAction
                label="Deactivate"
                icon={<CrossCircledIcon />}
                danger
                onClick={() => {
                  openModal(
                    <DeactivateCustomFieldModal
                      field={row.original}
                      hospitalGroupId={hospitalGroupId}
                      onDone={closeModal}
                    />,
                    { width: "520px", showCloseButton: true }
                  );
                }}
              />
            ) : (
              <RowAction
                label="Reactivate"
                icon={<CheckCircledIcon />}
                disabled={isUpdatingCustomField}
                onClick={() => {
                  handleReactivate(row.original);
                }}
              />
            )}
          </RowActions>
        ),
      },
    ],
    [hospitalGroupId, isUpdatingCustomField]
  );

  return (
    <TabStack>
      <SectionCard>
        <SectionHeader>
          <SectionTitleGroup>
            <SectionTitle>Custom fields</SectionTitle>
            <PageInfoTooltip title="Custom fields">
              Hospital-wide definitions; each ward chooses which to use
            </PageInfoTooltip>
          </SectionTitleGroup>
          <HeaderActions>
            <FilterButton
              variant="secondary"
              onClick={() =>
                openModal(
                  <AddFromTemplatesModal
                    hospitalGroupId={hospitalGroupId}
                    onDone={closeModal}
                  />,
                  {
                    width: "min(560px, 94vw)",
                    maxHeight: "90vh",
                    showCloseButton: true,
                  }
                )
              }
            >
              Add from presets
            </FilterButton>
            <FilterButton
              variant="primary"
              onClick={() =>
                openModal(
                  <CustomFieldModal
                    hospitalGroupId={hospitalGroupId}
                    onDone={closeModal}
                  />,
                  {
                    width: "min(560px, 94vw)",
                    maxHeight: "90vh",
                    showCloseButton: true,
                  }
                )
              }
            >
              New field
            </FilterButton>
          </HeaderActions>
        </SectionHeader>

        <FieldHint>
          A field defined here does nothing until a ward enables it — on the
          Wards tab, under Custom fields.
        </FieldHint>

        {isError && (
          <ErrorMessage>
            Failed to load this hospital&apos;s custom fields. Try refreshing
            the page.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[[CUSTOM_FIELDS_QUERY_KEY]]}>
            <TableSorted
              columns={columns}
              data={items}
              emptyStateText={
                isLoading ? (
                  <TableLoadingState />
                ) : (
                  "This hospital has no custom fields"
                )
              }
            />
          </TableWrapper>
          <TablePagination
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            exportCsv={{
              filename: `hospital-${hospitalGroupId}-custom-fields`,
              columns,
              rows: allItems,
            }}
          />
        </TableContainer>
      </SectionCard>
    </TabStack>
  );
}
